import React, { useEffect, useRef } from "react";

/**
 * حوار تأكيد عام - قابل لإضافة محتوى إضافي بالداخل (children) مثل حقل سبب
 * الرفض. يدعم: عنوان، شرح، إجراء رئيسي/ثانوي، إغلاق بـEscape، تركيز تلقائي
 * أول عنصر تفاعلي، وتعطيل الأزرار أثناء التحميل.
 */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "تأكيد",
  cancelLabel = "إلغاء",
  destructive = false,
  loading = false,
  disabled = false,
  onConfirm,
  onCancel,
  children,
}) {
  const dialogRef = useRef(null);
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;
    const firstFocusable = dialogRef.current?.querySelector(
      "button, [href], input, textarea, select"
    );
    firstFocusable?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onCancelRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby={description ? "confirm-dialog-desc" : undefined}
        dir="rtl"
        className="w-full max-w-md rounded-[var(--artika-radius-lg)] bg-white shadow-[var(--artika-shadow-lg)] p-5"
        style={{ fontFamily: "var(--artika-font)" }}
      >
        <h2 id="confirm-dialog-title" className="text-lg font-extrabold text-[var(--artika-text)] mb-1.5">
          {title}
        </h2>
        {description && (
          <p id="confirm-dialog-desc" className="text-sm text-[var(--artika-muted-text)] mb-3">
            {description}
          </p>
        )}

        {children}

        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-[var(--artika-radius-md)] text-sm font-bold border border-[var(--artika-border)] text-[var(--artika-text)] hover:bg-[var(--artika-surface-muted)] disabled:opacity-50 min-h-[44px]"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading || disabled}
            className={`px-4 py-2 rounded-[var(--artika-radius-md)] text-sm font-bold text-white disabled:opacity-50 min-h-[44px] ${
              destructive ? "bg-[var(--artika-danger)]" : "bg-[var(--artika-primary)]"
            }`}
          >
            {loading ? "جارٍ التنفيذ..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
